import { useEffect, useState, useCallback } from "react";
import { Pencil } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";
import SlidePanel from "../../components/admin/SlidePanel";
import { TextField, TextareaField, FormFooterActions } from "../../components/admin/FormFields";
import ImageUpload from "../../components/admin/ImageUpload";

export default function AdminFounders() {
  const [founders, setFounders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/founders").then((r) => setFounders(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const open = (f) => setEditing({ ...f });
  const close = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/founders", editing);
      toast.success("Founder updated");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-founders">
      <AdminHeader
        title="Founders"
        subtitle="Andrew and Slava — featured on the About page."
        count={founders.length}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {founders.map((f) => (
          <button
            key={f.id}
            data-testid={`founder-row-${f.slug}`}
            onClick={() => open(f)}
            className="group flex gap-5 text-left bg-[#151515] border border-[#D8D6D0]/10 hover:border-[#D8D6D0]/30 transition-colors p-5"
          >
            <div className="w-28 h-36 flex-shrink-0 overflow-hidden bg-[#0d0d0d] border border-[#D8D6D0]/10">
              {f.image_url ? (
                <img src={f.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${f.image_url}` : f.image_url} alt={f.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#D8D6D0]/30 font-serif-display text-5xl">
                  {f.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="font-serif-display text-2xl">{f.name}</div>
                <Pencil className="w-4 h-4 text-[#D8D6D0]/30 group-hover:text-[#F5F3EF] flex-shrink-0" />
              </div>
              <div className="overline text-[10px] text-[#D8D6D0]/55 mt-1">{f.role}</div>
              <p className="text-xs text-[#D8D6D0]/55 mt-3 line-clamp-3">{f.bio}</p>
            </div>
          </button>
        ))}
      </div>

      <SlidePanel
        open={!!editing}
        onClose={close}
        title={editing ? `Edit ${editing.name}` : "Founder"}
        subtitle="Appears on the public /about page."
        footer={
          <FormFooterActions onCancel={close} onSubmit={save} submitting={saving} />
        }
      >
        {editing && (
          <>
            <ImageUpload label="Portrait" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testId={`founder-${editing.slug}-image`} />
            <TextField id="name" label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
            <TextField id="role" label="Role / Title" value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })} required hint="e.g. Co-Founder & Director of Construction" />
            <TextareaField id="bio" label="Bio" value={editing.bio} onChange={(v) => setEditing({ ...editing, bio: v })} rows={6} required />
          </>
        )}
      </SlidePanel>
    </div>
  );
}
